<template>
<div>


  <ValidationObserver ref="observer" v-slot="{ invalid }">
    <div class="box">
      <div class="flex flex-wrap justify-center items-end -mx-2">
        <div class="w-full md:w-5/12 px-2">
          <label class="block text-lg font-bold mb-2" for="account-from">
            FROM ACCOUNT
          </label>
          <ValidationProvider
            rules="required"
            name="from account"
            v-slot="{ errors }"
          >
            <div class="relative">
              <select
                class="form-control pr-8"
                id="account-from"
                v-model="from"
                @change="selectedFrom"
              >
                <option :value="account" v-for="(account, i) in accountsFrom" :key="'accounts-from-' + i">
                  {{ account.name }} ({{ $formatMoney(account.balance) }})
                </option>
              </select>
              <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center text-xs px-2">
                <i class="fas fa-chevron-down"></i>
              </div>
            </div>
            <span class="text-sm text-red-500">{{ errors[0] }}</span>
          </ValidationProvider>
        </div>

        <div
          class="w-full md:w-2/12 text-center px-2"
          v-if="fromIsSelected || toIsSelected"
        >
          <button type="button" class="btn btn-purple py-2 px-4 mt-4" @click="flip">
            <i class="fas fa-exchange-alt"></i>
          </button>
        </div>

        <div class="w-full md:w-5/12 px-2">
          <label class="block text-lg font-bold mb-2" for="account-to">
            TO ACCOUNT
          </label>
          <ValidationProvider
            rules="required"
            name="to account"
            v-slot="{ errors }"
          >
            <div class="relative">
              <select
                class="form-control pr-8"
                id="account-to"
                v-model="to"
              >
                <option :value="account" v-for="(account, i) in accountsTo" :key="'accounts-to-' + i">
                  {{ account.name }} ({{ $formatMoney(account.balance) }})
                </option>
              </select>
              <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center text-xs px-2">
                <i class="fas fa-chevron-down"></i>
              </div>
            </div>
            <span class="text-sm text-red-500">{{ errors[0] }}</span>
          </ValidationProvider>
        </div>
      </div>

      <p class="text-xl font-bold mt-6 text-center">Amount</p>


      <ValidationProvider
        rules="required|currency"
        name="amount"
        v-slot="{ errors }"
      >
        <div class="mt-2 w-2/3 mx-auto">
          <div class="flex items-center justify-center">
            <div class="h-10 px-2 py-2 rounded-l border border-r-0 border-theme-200 light:border-theme-600 focus:outline-none">
              {{ config.currencySymbol }}
            </div>
              <input
                name="amount"
                type="text"
                id="amount"
                class="form-control form-no-outline h-10 border-r-0 rounded-none"
                autocomplete="off"
                v-model="amount"
                @input="autoAmount = null, displayAmount = parseFloat(amount.replace(',', ''))"
              >
            <button
              type="button"
              class="h-10 px-5 border border-r-0 border-purple-400 light:border-purple-600 focus:outline-none hover:bg-purple-800 hover:border-purple-700"
              :class="autoAmount === '100%' ? 'bg-purple-500 text-white' : ''"
              @click="setAmount('100%')"
            >100%</button>
            <button
              type="button"
              class="h-10 px-5 border border-purple-400 light:border-purple-600 rounded-r focus:outline-none hover:bg-purple-800 hover:border-purple-700"
              :class="autoAmount === '50%' ? 'bg-purple-500 text-white' : ''"
              @click="setAmount('50%')"
            >50%</button>
          </div>
          <span class="text-sm text-red-500">{{ errors[0] }}</span>
          <p v-show="insufficientBalance" class="text-sm text-red-500">Insufficient balance</p>
        </div>
      </ValidationProvider>

      <div class="flex flex-wrap mt-4 -mx-2 items-end justify-center">
        <div class="w-full md:w-2/3 px-2">
          <label for="note">Note</label>
          <textarea
            name="note"
            class="form-control"
            id="note"
            rows="3"
            v-model="note"
          ></textarea>
        </div>

        <div class="w-full md:w-1/3 px-2 flex flex-wrap justify-end items-center" v-if="canClear || canSave">
          <button
            type="button"
            class="btn btn-reset"
            @click="reset"
            v-if="canClear"
          >
            <i class="fa-xs fas fa-undo"></i>
            <span class="ml-1">CLEAR</span>
          </button>
          <button
            type="button"
            class="btn btn-save"
            @click="save"
            v-if="canSave"
          >
            <i class="fa-xs fas fa-check"></i>
            <span class="ml-2">PROCESS</span>
          </button>
        </div>
      </div>
    </div>
  </ValidationObserver>

</div>
</template>


<script>
import axios from 'axios'
import { mapActions, mapGetters } from 'vuex'

export default {
  data() {
    return {
      accounts: [],
      from: {},
      to: {},
      autoAmount: null,
      displayAmount: 0,
      amount: '',
      note: '',
    }
  },
  computed: {
    canClear() {
      return this.fromIsSelected || this.toIsSelected || this.amount > 0
    },
    canSave() {
      return !this.insufficientBalance && this.fromIsSelected && this.toIsSelected && this.amount > 0
    },
    toIsSelected() {
      return this.to && this.to.id
    },
    fromIsSelected() {
      return this.from && this.from.id
    },
    insufficientBalance() {
      if (!this.from) {
        return true
      }
      return this.from.balance < this.amount
    },
    accountsFrom() {
      if (!this.to) {
        return this.config.financialAccounts
      }
      return this.config.financialAccounts.filter(a => a.id !== this.to.id)
    },
    accountsTo() {
      if (!this.from) {
        return this.config.financialAccounts
      }
      return this.config.financialAccounts.filter(a => a.id !== this.from.id)
    },
    ...mapGetters({
      config: 'config/config',
    })
  },
  mounted() {
    this['config/getFinancialAccounts']()
  },
  methods: {
    reset() {
      this.from = {}
      this.to = {}
      this.autoAmount = null
      this.displayAmount = 0
      this.amount = ''
      this.note = ''
    },
    flip() {
      let from = this.from
      let to = this.to
      this.from = Object.assign({}, to)
      this.to = Object.assign({}, from)
      // reset amount
      this.autoAmount = null
      this.displayAmount = 0
      this.amount = ''
    },
    setAmount(value) {
      if (value === '100%') {
        this.autoAmount = '100%'
        this.amount = this.from.balance
      } else if (value === '50%') {
        this.autoAmount = '50%'
        this.amount = Math.floor((this.from.balance * 50 / 100) * 100) / 100
      } else {
        return false
      }
      this.displayAmount = this.amount.toFixed(2)
    },
    selectedFrom() {
      if (this.accountsTo.length === 1) {
        this.to = this.accountsTo[0]
      }
    },
    save() {
      let transfer = {
        from_account_id: this.from.id,
        to_account_id: this.to.id,
        value: this.amount,
        note: this.note,
      }
      axios.post('financial-transfers', transfer)
        .then(() => {
          this.reset()
          this.$swal.fire({
            title: 'Done',
            text: 'Transfer processed successfully',
            type: 'success',
          })
            .then(() => {
              this['config/getFinancialAccounts']()
                .then(() => {
                  this.$router.push('/administration/financial-account/list-transfers')
                })
            })
        })
        .catch((error) => {
          console.log(error)
        })
    },
    ...mapActions([
      'config/getFinancialAccounts'
    ])
  }
}
</script>
